/**
 * **联机对局屏。**
 *
 * 和热座那一版（`./game.ts`）共用**同一套版面装置与组件**，差别只有两处：
 *
 * | | 热座 | 联机 |
 * | --- | --- | --- |
 * | 视图从哪来 | `HotSeat.view`（本机 `project`） | `S3Session.view`（房主推来的） |
 * | 动作往哪去 | `HotSeat.act`（本地 `reduce`） | `S3Session.send`（RPC 给房主） |
 * | 换操作者 | 有（一台设备轮流） | 没有（你就是你） |
 *
 * ## ⚠️ 玩家侧不再需要房主发"合法动作清单"
 *
 * 早期版本由房主替每个客户端算一份 `options()`（`yourOptions`）随视图发下来，
 * 界面再从里面匹配。现在玩家侧改走 `ui/screens/tableIntent.ts` 的 `parsePlay`：
 * 手势 + `PlayerView`（手牌 / 配置 / 弃牌堆）直接解析成意图，合法性由 reducer
 * 兜底（发错了会被拒、手牌退回）。`options()` 只留给机器人 / 掉线兜底。
 *
 * 所以联机视图就是 `project()` 的输出本身，和热座用的是同一套推导。
 */

import { Container } from 'pixi.js';
import type { Ticker } from 'pixi.js';
import { richText, t } from '../../i18n';
import { cardNameResolver } from '../../core/cardText';
import { DEFAULT_COMBO_RULES } from '../../core/config';
import { CardFlip } from '../../render/game/cardFlip';
import { CardView } from '../../render/card/cardView';
import { faceSize } from '../../render/card/metrics';
import { CardTooltip, syncCardTooltip } from '../../render/game/cardTooltip';
import { CenteredStage } from '../../render/game/centeredCard';
import { PlayReveal, playSignature } from '../../render/game/playReveal';
import {
  DrawReveal,
  DYING_GROW_TO,
  REVEAL_CARD_HEIGHT,
  type ArrivalPose,
} from '../../render/game/drawReveal';
import { Hand } from '../../render/hand';
import { z } from 'zod';
import {
  DragFrameSchema,
  HandCountSchema,
  HoverFrameSchema,
  InterruptFrameSchema,
  RevealFrameSchema,
  ReorderFrameSchema,
  SIDE_DRAG,
  SIDE_HAND,
  SIDE_FAN_HOVER,
  SIDE_HOVER,
  SIDE_INTERRUPT,
  SIDE_REORDER,
  SIDE_REVEAL,
} from '../../net/side';
import { spectatorFrame } from '../../render/game/spectate';
import { SpectatorFan } from '../../render/game/spectatorFan';
import { travelProgress } from '../../render/game/interruptIntent';
import { InterruptRift } from '../../render/game/interruptRift';
import { reportEnergy } from '../../audio/report';
import { STEAL_ORDER_METHOD } from '../../host/rules';
import { createTableChrome, type TableChrome } from './gameTable';
import { publishGame } from '../../dev/registry';
import { ImpactParticles } from '../../render/game/impactParticles';
import { armActionOf, handCardAction, handIsLive, intentOf, parsePlay, playCards } from './tableIntent';
import type { GameAction } from '../../core/actions';
import { isInterrupt, type CardDef } from '../../core/card';
import type { CardTitle, CardUid, PlayerId } from '../../core/ids';
import type { EkView } from '../../host/rules';
import type { OnlineRoom } from '../../host/online';
import type { Screen, ScreenContext } from '../screen';
import { OVERLAY } from './ids';

/**
 * 手牌占画面底部的多高。
 *
 * ⚠️ 版面其余部分在 `gameTable.ts`（热座和联机共用），这里只剩手牌尺寸。
 */
const HAND_HEIGHT = 132;

export function createOnlineGameScreen(ctx: ScreenContext, online: OnlineRoom): Screen | null {
  const opened = online.session();
  if (opened === null) return null;
  // 提到非空之后就不再变（换一局会重建整屏），所以收紧一次类型
  const session = opened;
  const renderer = ctx.renderer;

  const container = new Container();
  container.label = 'screen-game-online';
  container.eventMode = 'static';
  const effects = new Container();

  const flips: CardFlip[] = [];

  /**
   * **抽牌动画**（结束回合抽到的那张）与还在飞的那几张。
   *
   * 和热座那一屏是同一套（`DrawReveal`），差别只有"视图从哪来"——
   * 说明都在 `game.ts` 的同名段里。
   */
  const reveals: Array<{
    view: DrawReveal;
    /**
     * **已经交班给"浮在正中那张牌"了吗**（和热座那一屏同一套，见 `game.ts`）。
     * 交班之后 `view` 被销毁，这一条只剩等 `centeredStage` 说它走了。
     */
    handedOff: boolean;
    readonly uid: CardUid;
    /** 这一张是"飞进手牌"那种（另一种是"停在正中"的致命牌） */
    readonly arrival: boolean;
    /** 这一张是不是造成濒死的那张 */
    readonly dying: boolean;
    /** 濒死已经放行了吗（飞到中点那一刻放） */
    dyingReleased: boolean;
    /** 落地那一记小 kick 放了没有（和热座那一屏同一套，见 `game.ts`） */
    landed: boolean;
  }> = [];
  let pendingReveals: Array<{
    readonly uid: CardUid;
    readonly def: CardDef;
    readonly hold: boolean;
    /** 这一手是谁抽的。联机里只用来判"是不是我自己抽的" */
    readonly actor: PlayerId;
  }> = [];
  /**
   * **正在飞的出牌**（`PlayReveal`）—— 和热座那一屏同一套，
   * 差别只有"视图从哪来"。触发 / 取消的说明见 `game.ts` 的同名段。
   */
  const plays: PlayReveal[] = [];
  /** 上一次摆过的出牌签名。`null` = 上一帧没有待结算出牌 */
  let lastPlaySignature: string | null = null;
  /**
   * **这一手打出的牌，起飞点在哪**（牌心，画布坐标）。
   *
   * 玩家是**从手里把牌拖出去**的，所以出牌动画该从拖拽位置起飞，而不是
   * 固定从某个下方锚点冒出来。`send()` 在动作发出去**之前**量下来；
   * `null` = 量不到（顺带覆盖“我是观众”那一路），回退到 `playOrigin()`。
   */
  let pendingPlayFrom: { readonly x: number; readonly y: number }[] | null = null;
  /** 正在飞进手牌的那几张：手牌为它们留了位，落地前不许喂给手牌 */
  let arriving: CardUid[] = [];
  /** 停在正中的那几张（致命牌）：牌在手里，但这一会儿不显示 */
  let inFlight: CardUid[] = [];
  /**
   * 手牌。
   *
   * ⚠️ **联机不从拖拽出牌**：拖拽在联机里是"理牌"（旁路通道的活），
   *    而出牌要发一个动作给房主 —— 那条路走底下的合法动作按钮。
   * ⚠️ 图层顺序：手牌 → 桌面装置（UI 盖在手牌上方，评审要求）→ 特效。
   */
  const hand = new Hand({
    input: ctx.input,
    renderer,
    faces: ctx.faces,
    bounds: { width: ctx.bounds.width, height: ctx.bounds.height },
    height: HAND_HEIGHT,
    cards: [],
    prewarm: false,
    // ⚠️ 拖出**手牌范围**才是出牌：范围内的拖动是理牌（旁路通道那条路）
    onPlay: (played) => playByCards(played),
  });
  container.addChild(hand.container);

  /** 桌面装置挂在这一层（换 UI 倍率时整块重建） */
  const chromeLayer = new Container();
  chromeLayer.label = 'table-chrome-layer';
  /** 齿轮菜单那一层：**加在最后**（"盖在整个屏幕之上"） */
  const menuLayer = new Container();
  menuLayer.label = 'table-menu-layer';
  /**
   * **悬停说明那一层**（和热座那一屏同一套，见 `render/game/cardTooltip.ts`）。
   *
   * ⚠️ 加在**最后**：说明是一块"浮在最上面的纸"，被手牌、装置、翻牌特效或者
   *    齿轮菜单压住都读不了。它是另一层，靠层的先后压住牌（手牌内部那套
   *    zIndex 排序管不到它）。
   */
  const tooltipLayer = new Container();
  tooltipLayer.label = 'table-tooltip-layer';
  /**
   * ★ **打击粒子**（和热座同一条，见 `game.ts`）。
   *
   * ⚠️ 加在 `effects` **之前**：粒子在"浮在正中的异常牌"**之下**（评审）。
   */
  const particles = new ImpactParticles();
  /**
   * ★ **观众视角：屏幕顶部那根上下反转的牌背扇。**
   *
   * 只在“轮到别人”时出现，镜像当前行动者的手牌（张数 / hover / 拖动 /
   * 按下露的那张脸）。数据走旁路通道，见 `render/game/spectatorHand.ts`。
   */
  const spectator = new SpectatorFan({
    renderer,
    registry: ctx.registry,
    faces: ctx.faces,
    // 和正常手牌同一套尺寸/间距
    height: HAND_HEIGHT,
  });
  // 牌扇按正常方式画在底部，整层再旋转 180° 呈现到顶部（见下）
  spectator.setOffset(ctx.bounds.width / 2, ctx.bounds.height - 18 - spectator.size.height);
  /**
   * ★ **旁观牌扇整层旋转 180°。**
   *
   * 它按**正常方式**渲染（画在底部、正常弧度、正常 hover/拖拽），然后整个
   * `spectator.view` 绕画布中心转 180° 呈现到顶部 —— 于是不需要逐项取反，
   * 也不会漏掉某个效果。
   */
  spectator.view.pivot.set(ctx.bounds.width / 2, ctx.bounds.height / 2);
  spectator.view.position.set(ctx.bounds.width / 2, ctx.bounds.height / 2);
  spectator.view.rotation = Math.PI;
  /**
   * ★ **打断预示（全屏）。**
   *
   * 有人把 [断点] 拖向屏幕中心时，从斜上方淡入一张 [断点]。数据走旁路
   * 通道（`SIDE_INTERRUPT`），纯视觉，见 `render/game/interruptRift.ts`。
   */
  const rifts = new InterruptRift({
    renderer,
    registry: ctx.registry,
    faces: ctx.faces,
    bounds: ctx.bounds,
  });
  container.addChild(
    chromeLayer,
    particles.view,
    effects,
    spectator.view,
    rifts.view,
    menuLayer,
    tooltipLayer,
  );

  let tooltipScale = ctx.settings.get().uiScale;
  let tooltip = new CardTooltip({
    scale: tooltipScale,
    bounds: ctx.bounds,
    cardNameOf: cardNameResolver(ctx.registry),
    comboRules: () => view()?.config.combos ?? DEFAULT_COMBO_RULES,
    loreSections: () => ctx.settings.get().loreSections,
  });
  // ⚠️ 必须把初始那份也挂进层 —— 以前只在"倍率变化时重建"那一条里挂，
  //    于是联机模式从头到尾都没有 tooltip（用户报的"hover tooltip 丢失"）。
  tooltipLayer.addChild(tooltip.container);

  /** 停在正中那张牌的管家（和热座共用一份实现，见 `render/game/centeredCard.ts`） */
  const centeredStage = new CenteredStage({
    input: ctx.input,
    faces: hand.faces,
    layer: effects,
    bounds: ctx.bounds,
    defOf: (uid) => defOfUid(uid),
    // ⚠️ hover 压低：这一张身后压着濒死层预烘的辉光/阴影（见 `game.ts`）
    feel: { hoverLift: 0.03, hoverScale: 0.05, hoverPitch: 4, hoverYaw: 5 },
  });

  /**
   * ★ **旁观他人濒死。**
   *
   * 特效走正常管线（`energyFactsOf.dying` 已扩到"任意玩家的 useRescue"），
   * 这里只额外放一张 **180° 的 [异常]** —— 它是"这是别人的濒死"与"我自己的濒死"
   * 的唯一区别（自己的那张由 `centeredStage` 正向渲染）。
   */
  const otherDying = new Container();
  otherDying.label = 'other-dying';
  otherDying.visible = false;
  effects.addChild(otherDying);
  let otherDyingShown: string | null = null;
  /** 0 → 1：别的玩家濒死时那张 [异常] 从"大小为 0"长到最终大小 */
  let otherDyingGrow = 1;
  let otherDyingCard: CardView | null = null;

  function syncOtherDying(v: EkView): void {
    const pending = v.pending;
    const title =
      pending !== null && pending.revealedTitle !== null && pending.player !== v.you
        ? pending.revealedTitle
        : null;
    if (title === null) {
      otherDying.visible = false;
      return;
    }
    if (otherDyingShown !== String(title)) {
      for (const child of [...otherDying.children]) child.destroy({ children: true });
      const def = ctx.registry.get(title);
      otherDyingCard = null;
      if (def === undefined) {
        otherDyingShown = null;
      } else {
        // ★ 和正常濒死同一尺寸（含 2 倍放大）；牌心放在容器原点 → 正中
        const size = faceSize(REVEAL_CARD_HEIGHT * DYING_GROW_TO);
        const view = new CardView({ texture: hand.faces.get(def, size), size });
        view.scale.set(0);
        otherDying.addChild(view);
        otherDyingCard = view;
        otherDyingShown = String(title);
        otherDyingGrow = 0;
        // ★ 和正常濒死同一记打击 + 粒子
        ctx.impact?.anticipate();
        ctx.impact?.hit();
        particles.burst({ x: ctx.bounds.width / 2, y: ctx.bounds.height / 2 });
      }
    }
    otherDying.position.set(ctx.bounds.width / 2, ctx.bounds.height / 2);
    otherDying.rotation = Math.PI;
    otherDying.visible = true;
  }

  /**
   * 摆悬停说明（每帧一次，`hand.update()` 之后）。
   *
   * ⚠️ UI 倍率变了要**重建**面板：字号/内边距是建的时候按倍率烘进去的。
   */
  function syncTooltip(dtMs: number): void {
    const s = ctx.settings.get().uiScale;
    if (s !== tooltipScale) {
      tooltip.destroy();
      tooltipScale = s;
      tooltip = new CardTooltip({
        scale: s,
        bounds: ctx.bounds,
        cardNameOf: cardNameResolver(ctx.registry),
        comboRules: () => view()?.config.combos ?? DEFAULT_COMBO_RULES,
      });
      tooltipLayer.addChild(tooltip.container);
    }
    tooltip.resize(ctx.bounds);
    /**
     * ⚠️ **先问"浮在正中那张牌"**（评审：hover 屏幕中央的濒死牌是查看它 lore
     *    的唯一方式）。它和手牌共用 `HoverSource`，说明面板一行都没改。
     */
    // 动作按钮的说明复用牌说明的同一块面板
    // 数字格的精确值说明优先于按钮说明（两者不会同时指着）
    const cell = chrome?.trackerCellTip() ?? null;
    const at = chrome?.actionTip() ?? null;
    const tip =
      cell !== null
        ? { title: cell.title, text: richText(cell.tip, cell.tipParams), box: cell.box }
        : at === null
          ? null
          : { title: at.title, text: richText(at.tip, at.tipParams), box: at.box };
    syncCardTooltip(tooltip, chrome?.trackerHover() ?? centeredStage.hovered() ?? hand, dtMs, tip);
  }

  /** 桌面装置。见 `gameTable.ts` 与热座那一屏的同名函数 */
  let chrome: TableChrome | null = null;
  let chromeScale = -1;

  function ensureChrome(): TableChrome {
    const s = ctx.settings.get().uiScale;
    if (chrome !== null && chromeScale === s) return chrome;
    chrome?.destroy();
    chrome = createTableChrome({
      ctx,
      renderer,
      defOf: (title) => ctx.registry.get(title),
      onEndTurn: () => undefined,
      onSettings: () => ctx.stack.openOverlay(OVERLAY.settings),
      // ★ 齿轮菜单里的「刷新同步」：客户端重取全量 / 房主重推全量
      onSync: () => online.sync(),
      onLeave: () => ctx.stack.pop(),
      onAction: (action) => void send(action),
      onPickBack: (slot) => pickBack(slot),
      // 旁路通道：抽牌人 hover 哪一位 → 直接发给被抽的那个人
      onFanHover: (slot) => onFanHover(slot),
      // ★ 点了队列里的名字 = 给这一手指定对象
      onPickTarget: (id) => pickTarget(id),
    });
    chromeScale = s;
    chromeLayer.addChild(chrome.container);
    // ★ 取牌扇要盖在出牌/翻牌特效之上（在 container 里它会被 effects 挡住）
    container.addChildAt(chrome.fanLayer, container.getChildIndex(effects) + 1);
    menuLayer.addChild(chrome.overlay);
    return chrome;
  }
  let dirty = false;
  let destroyed = false;
  let offView: (() => void) | null = null;
  let offEvent: (() => void) | null = null;
  let offStatus: (() => void) | null = null;
  let offAsk: (() => void) | null = null;
  let offError: (() => void) | null = null;
  let offSideHover: (() => void) | null = null;
  let offSideFanHover: (() => void) | null = null;
  let offSideDrag: (() => void) | null = null;
  let offSideReorder: (() => void) | null = null;
  let offSideHand: (() => void) | null = null;
  let offSideReveal: (() => void) | null = null;
  let offSideInterrupt: (() => void) | null = null;
  /** dev 下把这一局挂到 dev/registry；销毁时摘掉 */
  let unpublish: (() => void) | null = null;

  let lastHandOrder: CardUid[] = [];
  /** 手牌"本来"该不该可操作（记牌器开着时要再压一层，见 updateFrame） */
  let handLive = false;
  /** 正在拖的那一位（松手时要发一个"归位"） */
  /**
   * 拖动这张牌**上一次广播的位次**。
   *
   * ⚠️ 拖动期间本地一换位，就**立刻**补一条 `SIDE_REORDER`，位次跟着更新 ——
   * 这样位移永远套在"那张牌现在的位置"上。只冻结起点、等松手才重排的话，
   * 换位后 `dx/dy` 是相对**新槽位**算的，接收端却还套在旧槽位上，那张牌会
   * 原地抽搐（用户报的）。
   */
  let dragFrom: number | null = null;
  /** 上一次报给对面的 hover（变了才发，不然 60 帧全是重复） */
  let lastHoverSlot: number | null = null;
  /** 上一次报给观众的手牌数量 */
  let lastBroadcastCount = -1;
  /** 上一位"按下露牌"的位次（null = 没露） */
  let lastRevealSlot: number | null = null;
  /** 上一次"轮到的是我"的标记：换到我的第一帧要强制重报一次数量 */
  let lastSentTurn: PlayerId | null = null;
  /** 我正在广播 [断点] 的预示行程吗（松手那一帧要发一条 0 收回） */
  let interruptDragging = false;

  const view = (): EkView | null => session.view() as EkView | null;

  // ============================================================
  // 版面
  // ============================================================

  /**
   * 重建版面。
   *
   * 和热座那一屏一样：桌面装置（回合队列 / 抽牌堆 / 弃牌堆 / 结束回合 /
   * 齿轮）全在 `gameTable.ts` 里，这一屏只负责"视图从哪来、动作往哪去"。
   */
  function build(): void {
    const v = view();
    if (v === null) return;
    // ★ 每一版视图都报一次分 —— 音乐跟着局面走。
    //    放在这里而不是 `onView` 里，是因为 `build()` 本来就是"视图变了"的落点
    //    （`rebuildSoon` 微任务合并），报分顺便搭车，两条路径不会漂开。
    reportEnergy(ctx.energy, v, ctx.registry);
    // ⚠️ 先作废过期的选择（换人 / 牌被抢走 / 窗口开了），再算 intent ——
    //    顺序反了会把一个已经打不出去的选择算进 targets 里
    // ⚠️ 顺序要紧：**先**摆动画（手牌这才知道要给哪张牌留位），再喂手牌
    spawnPendingReveals();
    validateSelection();
    const chromeNow = ensureChrome();
    chromeNow.update(v, currentIntent(v));
    // ★ 目标高亮只给**被选中的那个人**看 —— 联机里每个客户端都收到同一份
    //   `resolution.play.target`，不按 `v.you` 过滤的话，八竿子打不着的玩家
    //   屏幕边缘也会亮起来（用户：「不符合这一情形却被触发」）。
    //   hover 的那一份仍由队列自己报（行动者本机能看到预览）。
    const targetNow = selection?.target ?? v.resolution?.play.target ?? null;
    chromeNow.setTargetHighlight(targetNow !== null && targetNow === v.you ? targetNow : null);
    // ★ 再摆出牌动画：视图里出现新的待结算出牌时飞一张（和热座同一套）。
    //   ⚠️ 必须在 `update()` **之后**：落点 `discardTop()` 是 layout 算出来的
    syncPlayReveals(v);
    /**
     * ★ **只有"我能出牌"的时候手牌才听 hover / 能拖**（和热座同一条，
     * 判据在 `handIsLive()` 里，见 `game.ts`）：`options()` 里有没有用到
     * 这手牌的选项 —— 出牌、打断、交牌、濒死时用救命牌都算。
     */
    // ★ 判据全部从视图推导（响应窗口里有没有 [断点]、濒死有没有 Catch、是不是
    //   我的自由回合一目了然）—— 不再依赖 options()。
    // ★ 已经举起来的那一手必须保持可打：松手要能提交（见 handIsLive 的 `armed`）
    handLive = handIsLive(v, ctx.registry, armed !== null);
    hand?.setInteractive(!(chrome?.trackerOpen() ?? false));
    hand?.setPlayable(handLive);
    syncHand(v);
    applyHidden();
    syncSpectator(v);
    // ★ 旁观他人濒死：那张 180° 的 [异常] 要跟着每一版视图更新。
    //   （之前只在 onEnter 调了一次，所以濒死发生在进了屏之后就永远不出现。）
    syncOtherDying(v);
  }

  /**
   * ★ **顶部那根观众牌扇常驻**：轮到别人时镜像他的那手牌，其余时候渲染为空。
   *
   * ⚠️ 不再 `visible = false` —— 节点一直在，只是 `setEmpty()`。数据来时由
   *    旁路通道 `setCount/setHover/...` 灌进去。
   */
  function syncSpectator(v: EkView): void {
    const actor = v.turn?.player;
    const watching = actor !== undefined && actor !== v.you;
    if (watching) return;
    // 轮到我自己 / 没有行动者：清空内容，避免下一位接上之前先看到残影
    spectator.setEmpty();
  }

  // ============================================================
  // 动作与同步
  // ============================================================

  /**
   * 点了第 `slot` 张牌背 = 抽这一张。**发一个 index 给房主**。
   *
   * ⚠️ 发的是**位次**，不是牌的 uid：顺序是"我这一手"的事，
   * 房主只负责按位次去拿（连它也不知道那是哪张牌）。
   */
  function pickBack(slot: number): void {
    const v = view();
    if (v === null) return;
    if (v.pending?.kind !== 'stealCard') {
      // 点击落空：视图已经不在抽牌抉择上了（对手先动了 / 这局结束了）。
      // 打出来是为了让"点了没反应"有据可查，而不是静默吞掉。
      console.warn(t('game.online.stalePick'), v.pending);
      return;
    }
    void send({
      t: 'decide',
      actor: v.you,
      decision: { kind: 'stealCard', index: slot },
    });
  }

  /**
   * 抽牌人 hover 了第 `slot` 张牌背 → **直接发给被抽的那个人**（不走房主）。
   *
   * 评审：「顺序的调换实时应用于抽牌人屏幕上那一手牌背」+「双向 hover」。
   * 载荷里只有位次，没有牌的 id —— 收到的人再看也看不出牌是什么。
   */
  function onFanHover(slot: number | null): void {
    // 取牌扇 = 被抽那人的手牌，走独立的通道（不能和"行动者自己的手牌"混）
    online.side.broadcast(SIDE_FAN_HOVER, { slot });
  }

  /** 发一个动作给房主。被拒就把原因写进日志 —— 界面上要看得见 */
  /**
   * 「现在该让玩家点哪儿」。和热座共用 `tableIntent.ts` 那张表。
   *
   * ⚠️ 联机的手牌**拖拽是"理牌"**（旁路通道那条路），出牌不能靠拖 ——
   * 所以这里额外把"能打的那几张牌"记下来，玩家点手牌时按那张牌**单选**去打
   * （见 `onHandTap`）。
   */
  function currentIntent(v: EkView): ReturnType<typeof intentOf> {
    const intent = intentOf(v, ctx.registry);
    // ★ 五张不同名：现在等玩家点弃牌堆里要捞的那张
    if (fivePick !== null && fivePick.size > 0) {
      return { ...intent, targets: [], insert: [], pick: fivePick };
    }
    // ⚠️ 只有"拖出去之后还差对象"的时候队列里的名字才可以点（和热座同一条规矩）
    const targeting = selection !== null && intent.targets.length > 0;
    return { ...intent, targets: targeting ? intent.targets : [] };
  }

  /**
   * **玩家把手牌拖出去了。**
   *
   * 和热座那一屏共用 `parsePlay()`：拖出来的牌直接从视图解析成意图；还差
   * "对谁 / 要哪个牌名 / 捞哪张"就继续问，凑齐了发给房主。
   *
   * ⚠️ 联机的手牌拖拽**同时**是"理牌"（旁路通道那条路）—— 拖动手牌范围内是
   * 理牌，**拖出手牌范围**才是出牌（`Hand` 的 `onPlay` 只在越线时回调）。
   */
  function playByCards(played: readonly CardDef[]): void {
    if (played.length === 0) return;
    const v = view();
    if (v === null) return;
    // ★ 已经举起来了 → 松手就是提交这一手（不再从 options() 里重算）
    if (armed !== null) {
      const action = armed;
      armed = null;
      void send({ t: 'turn/play', actor: action.actor, play: action.play });
      return;
    }
    const used = uidsOfPlayed(played);
    // ★ 拖出来的牌在手上找不到 —— 这一手不成立（视图刚换过）。牌必须回去
    if (used.length === 0) {
      hand.revert();
      return;
    }

    if (selection !== null && selection.target !== null) {
      const first = used[0];
      const card = first === undefined ? undefined : v.yourHand.find((c) => c.uid === first);
      if (card !== undefined) {
        const done = parsePlay(v, selection.cards, { target: selection.target, namedTitle: card.title }, ctx.registry);
        if (done.kind === 'act') {
          selection = null;
          void send(done.action);
          return;
        }
      }
      cancelSelection();
      refresh();
      return;
    }

    // ★ 单张的"非出牌"动作（用救命牌 / 交牌 / 打断）：手势直接映射成动作
    if (used.length === 1) {
      const only = used[0];
      const decided = only === undefined ? null : handCardAction(v, only, ctx.registry);
      if (decided !== null) {
        void send(decided);
        return;
      }
    }

    // ★ 出牌：把"拖了哪几张"解析成一条意图（目标 / 牌名 / 捞哪张由视图推导）
    const result = parsePlay(v, used, {}, ctx.registry);
    if (result.kind === 'pick') {
      fivePick = result.picks;
      refresh();
      return;
    }
    if (result.kind === 'act') {
      void send(result.action);
      return;
    }
    if (result.kind === 'target') {
      selection = { cards: used, target: null };
      openChooser(used, result.targets);
      refresh();
      return;
    }

    // ★ 解析不出来（不合法的一手）→ 手牌退回拖出去之前
    hand.revert();
  }

  /**
   * **作废当前那一手。** 三处会用到：点了打不出去的一手、按 Esc、
   * 以及视图换了之后那一手解析不出来了。
   */
  function cancelSelection(): void {
    fivePick = null;
    if (selection === null) return;
    selection = null;
    hand.revert();
  }

  /**
   * 视图一换就检查那一手还算不算数。
   *
   * 换人、被抢走一张、响应窗口打开，都会让选好的那一手不再解析得出来 ——
   * 用 `parsePlay` 对照当前视图再问一遍就知道了。
   */
  function validateSelection(): void {
    if (selection === null) return;
    const v = view();
    if (v === null) return;
    const query = selection.target === null ? {} : { target: selection.target };
    const probe = parsePlay(v, selection.cards, query, ctx.registry);
    if (probe.kind === 'none') cancelSelection();
  }

  /** 拖出去的那几张 → uid（同名多张时按手牌顺序取最前面的，等价） */
  function uidsOfPlayed(played: readonly CardDef[]): CardUid[] {
    const v = view();
    if (v === null) return [];
    const pool = v.yourHand.map((card) => card.uid);
    const used: CardUid[] = [];
    for (const def of played) {
      const at = pool.findIndex((uid) => v.yourHand.find((c) => c.uid === uid)?.title === def.id);
      if (at < 0) continue;
      const [uid] = pool.splice(at, 1);
      if (uid !== undefined) used.push(uid);
    }
    return used;
  }

  /** 还差"对谁"的那一手 */
  /** 已经"举起来"的那一手（松手前就开打断窗口）；null = 没举 */
  let armed: Extract<GameAction, { t: 'turn/arm' }> | null = null;
  let selection: { cards: readonly CardUid[]; target: PlayerId | null } | null = null;
  /** 五张不同名：5 张已选好，正等玩家点弃牌堆里要捞的那张 */
  let fivePick: ReadonlyMap<CardUid, GameAction> | null = null;

  /**
   * **把候选铺到画面中央**（目标 + 三张同名时还要牌名）。见 `game.ts` 同名函数。
   */
  function openChooser(cards: readonly CardUid[], targets: readonly PlayerId[]): void {
    const v = view();
    if (v === null) return;
    const names = targets.map((id) => ({
      id,
      name: v.players.find((player) => player.id === id)?.name ?? id,
    }));
    const first = targets[0];
    const probe =
      first === undefined
        ? ({ kind: 'target' } as const)
        : parsePlay(v, cards, { target: first }, ctx.registry);
    const titles = probe.kind === 'name' ? probe.titles : null;
    ensureChrome().choosePlay({
      targets: names,
      titles,
      onPick: (target, title) => finishChoice(cards, target, title),
    });
  }

  /** 选好目标（和牌名）之后收尾；解析不出来就整手作废 */
  function finishChoice(cards: readonly CardUid[], target: PlayerId, title: CardTitle | null): void {
    const v = view();
    if (v === null) return;
    const done = parsePlay(v, cards, { target, namedTitle: title }, ctx.registry);
    if (done.kind === 'act') {
      selection = null;
      void send(done.action);
      return;
    }
    cancelSelection();
    refresh();
  }

  /** 队列里点了一个人 = 给这一手指定对象（居中面板被挡住时的兜底） */
  function pickTarget(id: PlayerId): void {
    if (selection === null) return;
    const v = view();
    if (v === null) return;
    const cards = selection.cards;
    const done = parsePlay(v, cards, { target: id }, ctx.registry);
    if (done.kind === 'act') {
      selection = null;
      void send(done.action);
      return;
    }
    selection = { ...selection, target: id };
    if (done.kind === 'name') {
      openChooser(cards, [id]);
      return;
    }
    refresh();
  }

  /** 重画（选择状态变了） */
  function refresh(): void {
    build();
  }

  /**
   * 发一个动作给房主。
   *
   * ⚠️ **被拒要把手牌退回去。** 界面只发自己解析得出来的动作，但
   * "房主拒了"这件事是可能发生的
   * （状态在路上的这几百毫秒里变了，比如对面先打断）。不退的话，
   * 那几张牌已经从扇形里摘掉了，看上去像"打出去了但没人理"。
   *
   * 评审：「打出手牌的动作不会验证动作是否合法，也不会适时同步和回退状态。」
   */
  async function send(action: GameAction): Promise<void> {
    fivePick = null;
    // ★ 量下这一手牌现在在屏幕上的位置（还在拖拽落点），给出牌动画当起飞点
    if (action.t === 'turn/play') {
      // ⚠️ 单张出牌的 `play` 用的是 `card`（不是 `cards`）—— 以前只读 `cards`，
      //    于是单张出牌永远量不到起飞点、只能回退到固定锚点。
      capturePlayFrom(playCards(action.play));
    } else {
      pendingPlayFrom = null;
    }
    const ack = await session.send(action).catch((error: unknown) => {
      console.warn(t('game.online.sendFailed'), action, error);
      return { ok: false as const, reason: t('game.online.sendFailedReason') };
    });
    if (ack.ok) return;
    // ★ 房主拒了就必须让人看得见 —— 否则界面只是把手牌退回去，玩家会以为
    //   是“点了没用”（用户要求：host 拒绝并回退时把错误打在 console）。
    console.warn(t('game.online.rejected'), action, t('game.online.reasonPrefix'), ack.reason);
    pendingPlayFrom = null;
    cancelSelection();
    hand.revert();
    rebuildSoon();
  }

  /**
   * **把打出的牌现在的牌心位置量下来。** 见 `pendingPlayFrom`。
   *
   * `lastHandOrder[i]` 是喂给手牌的第 i 张牌（uid），`hand.view.boxOf(i)`
   * 是它在画布上的包围盒 —— 拖出去的牌此刻正停在拖拽落点。量不齐就整体
   * 放弃（`null`），让调用方回退到固定锚点。
   */
  function capturePlayFrom(uids: readonly CardUid[]): void {
    if (hand === null || uids.length === 0) {
      pendingPlayFrom = null;
      return;
    }
    const out: { readonly x: number; readonly y: number }[] = [];
    for (const uid of uids) {
      const at = lastHandOrder.indexOf(uid);
      const box = at >= 0 ? hand.view.boxOf(at) : null;
      if (box === null) continue;
      out.push({ x: box.x + box.width / 2, y: box.y + box.height / 2 });
    }
    pendingPlayFrom = out.length === uids.length ? out : null;
  }

  function syncHand(v: EkView): void {
    if (hand === null) return;
    // ⚠️ 正在飞进来的那几张先别喂：手牌已经给它们留好位置了（`reserveArrival`）
    const flying = new Set(arriving);
    const list = v.yourHand.filter((card) => !flying.has(card.uid));
    // ⚠️ `lastHandOrder` 必须和喂进去的牌一一对应（查不到定义的会被跳过）
    const defs: CardDef[] = [];
    const fed: CardUid[] = [];
    for (const card of list) {
      const def = ctx.registry.get(card.title);
      if (def === undefined) continue;
      defs.push(def);
      fed.push(card.uid);
    }
    const same =
      fed.length === lastHandOrder.length && fed.every((uid, i) => uid === lastHandOrder[i]);
    if (same) return;
    lastHandOrder = fed;
    hand.setCards(defs);
  }

  /** 查看某张牌的定义。手牌和弃牌堆都查（致命牌可能刚被丢进弃牌堆） */
  function defOfUid(uid: CardUid): CardDef | undefined {
    const v = view();
    if (v === null) return undefined;
    const pooled = [...v.yourHand, ...v.discardPile].find((card) => card.uid === uid);
    return pooled === undefined ? undefined : ctx.registry.get(pooled.title);
  }

  /** 停在正中的那几张：占着手牌的位、但不显示。按 uid 重算索引，见 `game.ts` */
  function applyHidden(): void {
    const v = view();
    if (v === null) return;
    const indices: number[] = [];
    for (const uid of inFlight) {
      const index = v.yourHand.findIndex((card) => card.uid === uid);
      if (index >= 0) indices.push(index);
    }
    // ★ 塞回异常时，那张异常由正中的浮牌代表，不该再出现在牌扇里
    const pending = v.pending;
    if (pending?.kind === 'insertLethal' && pending.revealedTitle !== null) {
      for (let i = 0; i < v.yourHand.length; i++) {
        if (v.yourHand[i]?.title === pending.revealedTitle && !indices.includes(i)) indices.push(i);
      }
    }
    hand.setHidden(indices);
  }

  /**
   * 把攒下的抽牌摆成动画。评审那三段动作见 `render/game/drawReveal.ts`。
   *
   * 和热座那一屏共用同一套协议（手牌留位 → 飞行 → 交接），说明写在那边。
   * 差别只有一个：联机**没有换操作者**这回事，所以不需要钉视角。
   */
  function spawnPendingReveals(): void {
    if (pendingReveals.length === 0) return;
    const pending = pendingReveals;
    pendingReveals = [];

    const chrome_ = ensureChrome();
    const from = chrome_.drawTop();
    // ★ 中点是**画布正中**（和濒死辉光同心），不是 `FAN_CENTER`，见 `game.ts`
    const center = { x: ctx.bounds.width / 2, y: ctx.bounds.height / 2 };

    for (const entry of pending) {
      // ⚠️ 手牌给的是**姿态**（坐标 + 旋转 + 尺寸），不是这里估的一个坐标
      let to: ArrivalPose | null = null;
      if (!entry.hold) {
        const pose = hand.reserveArrival();
        if (pose !== null) {
          to = { x: pose.x, y: pose.y, rotation: pose.roll, scale: pose.scale };
          arriving.push(entry.uid);
        }
      }
      const view_ = new DrawReveal({
        renderer,
        def: entry.def,
        height: REVEAL_CARD_HEIGHT,
        from,
        center,
        to,
        dying: entry.hold,
      });
      if (entry.hold) {
        inFlight.push(entry.uid);
        // ★ 把濒死压住，等飞到中点再放行（评审：「…动画完成后…随即生效」）
        ctx.energy?.holdDying(true);
        // ★ 渐强从这里开始（和热座同一条，见 `game.ts`）
        ctx.impact?.anticipate();
      }
      effects.addChild(view_.view);
      reveals.push({
        view: view_,
        handedOff: false,
        uid: entry.uid,
        arrival: to !== null,
        dying: entry.hold,
        dyingReleased: false,
        landed: false,
      });
    }
    applyHidden();
  }

  /**
   * **待结算出牌出现时，摆一拨出牌动画。** 和热座那一屏同一套，见 `game.ts`。
   */
  function syncPlayReveals(view_: EkView): void {
    const play = view_.resolution?.play ?? null;
    const signature = playSignature(play);
    if (signature === null) {
      lastPlaySignature = null;
      return;
    }
    if (signature === lastPlaySignature) return;
    lastPlaySignature = signature;

    const chrome_ = ensureChrome();
    const origin = chrome_.playOrigin();
    const center = { x: ctx.bounds.width / 2, y: ctx.bounds.height / 2 };
    const count = play === null ? 0 : play.cards.length;
    const captured = pendingPlayFrom;
    let index = 0;
    for (const card of play?.cards ?? []) {
      const def = ctx.registry.get(card.title);
      if (def === undefined) continue;
      /**
       * ★ **从拖拽落点起飞**（我打的那一路）；量不到就退回固定锚点
       *   （观众那一路：它看不到 actor 的真实手牌位置）。
       *   两张以上时在起飞处**横向错开一点**，不然它们完全重叠成一张。
       */
      const base = captured?.[index] ?? origin;
      const spread = count > 1 ? (index - (count - 1) / 2) * (REVEAL_CARD_HEIGHT * 0.35) : 0;
      index += 1;
      const reveal = new PlayReveal({
        renderer,
        def,
        height: REVEAL_CARD_HEIGHT,
        faces: hand.faces,
        from: { x: base.x + spread, y: base.y },
        // ★ 中心也按张数横排错开 —— 否则多张飞到中心会完全重叠成一张
        center: { x: center.x + spread, y: center.y },
      });
      effects.addChild(reveal.view);
      plays.push(reveal);
    }
    pendingPlayFrom = null;
  }

  /**
   * 每帧推出牌动画。`cancelled` 由最新视图喂进来（打断链奇偶）。
   */
  function updatePlays(ticker: Ticker): void {
    if (plays.length === 0) return;
    const resolution = view()?.resolution ?? null;
    for (let i = plays.length - 1; i >= 0; i--) {
      const reveal = plays[i];
      if (reveal === undefined) continue;
      if (resolution !== null) {
        reveal.setCancelled(resolution.nopeChain.length % 2 === 1);
        reveal.setNopeCount(resolution.nopeChain.length);
        reveal.setWindowOpen(true);
      } else if ((view()?.pending ?? null) === null) {
        // ⚠️ 别在这一帧再喂 `setCancelled(false)`：它会把最后一帧的奇偶覆盖掉
        reveal.setWindowOpen(false);
      }
      // else：窗口已关，但这一手还欠一个抉择（给牌 / 抽牌 / 捞牌）——
      //       牌得**继续停在中央**等它结算完再消失（用户：打出去就直接消失了）
      reveal.update(ticker);
      if (!reveal.finished) continue;
      reveal.destroy();
      plays.splice(i, 1);
    }
  }

  /** 每帧推抽牌动画，走完就和手牌交接（飞进手牌那种）或解封（停正中那种） */
  function updateReveals(ticker: Ticker): void {
    if (reveals.length === 0) return;
    let unsealed = false;
    for (let i = reveals.length - 1; i >= 0; i--) {
      const entry = reveals[i];
      if (entry === undefined) continue;
      // ★ 已经交班了：它的去留由 `centeredStage` 说了算（淡出完 → finished）
      if (entry.handedOff) {
        if (!centeredStage.finished) continue;
      } else {
        entry.view.update(ticker);
        // ★ **落地那一记小 kick**（牌砸在桌上）。和热座那一屏同一套，见 `game.ts`
        if (entry.dying && !entry.landed && entry.view.arrived) {
          entry.landed = true;
          ctx.impact?.land();
        }
        /**
         * ★ **撑满的那一刻 = 主打击**（`docs/dying-impact.md`）。
         *
         * ⚠️ 原来是 `arrived`（刚飞到中点）就放开 `holdDying` —— 红世界、故障、
         *    BGM 全在牌还只有 0.82 倍时亮起来，等牌撑满反而什么都没发生。
         *    改到 `holding` 之后，视觉 / 音乐 / 冻结 / 白闪 / 震动落在同一帧。
         */
        if (entry.dying && !entry.dyingReleased && entry.view.holding) {
          entry.dyingReleased = true;
          ctx.energy?.holdDying(false);
          ctx.impact?.hit();
          particles.burst({ x: ctx.bounds.width / 2, y: ctx.bounds.height / 2 });
        }
        /**
         * ★ **换手：翻牌那一段走完 → 交给"浮在正中的那张牌"。**
         *
         * 和热座那一屏是同一条路（评审：浮在屏幕中央的牌要有手牌的一切属性）。
         * 交接点在"停稳"那一刻，两边姿态完全一样，所以看不出来。
         */
        if (entry.dying && entry.view.holding) {
          entry.handedOff = true;
          centeredStage.start(entry.uid);
          entry.view.destroy();
          continue;
        }
        if (!entry.view.finished) continue;
        entry.view.destroy();
      }
      reveals.splice(i, 1);

      if (entry.arrival) {
        // ★ 新牌到位：手牌切成"加完牌"的状态，飞行的这一张删掉
        const at = arriving.indexOf(entry.uid);
        if (at >= 0) {
          arriving.splice(at, 1);
          hand.commitArrival();
          const v = view();
          lastHandOrder = [];
          if (v !== null) syncHand(v);
        }
      } else {
        const at = inFlight.indexOf(entry.uid);
        if (at >= 0) {
          inFlight.splice(at, 1);
          unsealed = true;
        }
      }
    }
    if (unsealed) applyHidden();
  }

  /**
   * ★ **[断点] 拖拽 → 全屏预示的行程。**
   *
   * 只有“手里那张是 [断点]、且当前有一个可打断的出牌（`resolution`）”时才发。
   * 行程用 `travelProgress` 投影到“按下位置 → 屏幕中心”的方向上 —— 横着乱拖
   * 不涨；拖过头则由接收端取对数（`interruptProgress`）。
   *
   * ⚠️ 和 `SIDE_DRAG`（重放拖拽）是两条通道：这一条只讲“离屏幕中心还有多远”，
   *    不讲牌、也不进状态。
   */
  function syncInterruptBroadcast(cardIndex: number, dx: number, dy: number): void {
    const v = view();
    const def = hand?.defAt(cardIndex) ?? null;
    const active = v !== null && v.resolution !== null && def !== null && isInterrupt(def);
    if (!active) {
      if (interruptDragging) {
        interruptDragging = false;
        online.side.broadcast(SIDE_INTERRUPT, { progress: 0 });
      }
      return;
    }
    const box = hand?.view.boxOf(cardIndex) ?? null;
    const h = hand?.size.height ?? 0;
    if (box === null || h <= 0) return;
    const current = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
    // `dx` / `dy` 是相对静止位的位移（单位：牌高），减回去就是“按下时的位置”
    const start = { x: current.x - dx * h, y: current.y - dy * h };
    const raw = travelProgress({
      start,
      current,
      target: { x: ctx.bounds.width / 2, y: ctx.bounds.height / 2 },
    });
    interruptDragging = true;
    online.side.broadcast(SIDE_INTERRUPT, { progress: raw });
  }

  /**
   * 理牌 → 旁路通道。
   *
   * 评审：「调换手牌顺序等动作和 hover 的同步数据**不经过 host**，
   * 直接在各客户端间 p2p 传输」。所以这里**不发动作**，
   * 只广播一个位次变化 —— 它不进状态、也不改结果，
   * 只是让"被抽牌的人"那一手牌背在所有人屏幕上跟着动。
   */
  function flushHandOrder(): void {
    const v = view();
    if (hand === null || v === null) return;
    // ★ **轮到我的第一帧强制重报一次手牌数量。**
    //    "数量没变就跳过"会让观众那根扇一直停在**上一个行动者**身上 ——
    //    下一个行动者若手牌数恰好和它上次报的一样，就永远等不到新的 SIDE_HAND。
    if (v.turn?.player === v.you) {
      if (lastSentTurn !== v.you) {
        lastSentTurn = v.you;
        lastBroadcastCount = -1;
        lastRevealSlot = null;
      }
    } else {
      lastSentTurn = null;
    }
    const order = hand.order;

    // ★ **松手前举牌**：拖到"松手会打出"的有效高度就发 turn/arm ——
    //   它只开打断窗口、不动牌；对手从这一刻起就能打断。
    const out = hand.dragOut();
    if (out.length > 0) {
      if (armed === null) {
        const action = armActionOf(v, uidsOfPlayed(out), ctx.registry);
        if (action !== null && action.t === 'turn/arm') {
          armed = action;
          void send(action);
        }
      }
    } else if (armed !== null) {
      void send({ t: 'turn/arm/cancel', actor: armed.actor });
      armed = null;
    }

    // ⓪ 观众广播：手牌**数量** + "按下露牌"。即使手牌正在重建也要发 ——
    //    数量变了观众那根扇要跟上。
    const frame = spectatorFrame({
      count: hand.count,
      order,
      hovered: hand.hovered,
      held: hand.held,
      drag: null,
      titleOf: (i) => hand?.defAt(i)?.id,
    });
    if (frame.count !== lastBroadcastCount) {
      lastBroadcastCount = frame.count;
      online.side.broadcast(SIDE_HAND, { count: frame.count });
    }
    const revealSlot = frame.reveal?.slot ?? null;
    if (revealSlot !== lastRevealSlot) {
      lastRevealSlot = revealSlot;
      online.side.broadcast(SIDE_REVEAL, { slot: revealSlot, title: frame.reveal?.title ?? null });
    }

    if (order.length !== lastHandOrder.length) return;

    // ① 拖动**期间**逐帧发位移（评审要的是"重现拖拽动作"，
    //    不是"换位完成之后补一个切换动画"）
    const drag = hand.dragInfo();
    if (drag !== null) {
      // ★ [断点] 的预示行程：和“重放拖拽”分开一条通道（它只关乎全屏动画）
      syncInterruptBroadcast(drag.card, drag.dx, drag.dy);
      const place = order.indexOf(drag.card);
      if (dragFrom === null) dragFrom = place;
      // ★ **拖动中换位要实时同步**：不然接收端还按旧位次套位移，那张牌会原地抽搐。
      //   位次变了就先补一条重排，再把位移套到新位次上。
      if (place >= 0 && place !== dragFrom) {
        online.side.broadcast(SIDE_REORDER, { from: dragFrom, to: place });
        dragFrom = place;
      }
      if (place >= 0) {
        online.side.broadcast(SIDE_DRAG, {
          slot: place,
          x: drag.x,
          y: drag.y,
          armed: armed !== null,
          title: hand.defAt(drag.card)?.id ?? null,
        });
      }
      return;
    }
    // ★ 松手 / 不再拖 [断点] → 收回全屏预示
    if (interruptDragging) {
      interruptDragging = false;
      online.side.broadcast(SIDE_INTERRUPT, { progress: 0 });
    }
    // 松手：重排已经在拖动中逐帧同步过了，这里只发"归位"
    if (dragFrom !== null) {
      online.side.broadcast(SIDE_DRAG, { slot: null, x: 0, y: 0, armed: false, title: null });
      dragFrom = null;
    }

    // ② 本地 hover：我指着自己第几张 → 对方那一扇牌背也抬起来
    const hovered = hand.hovered;
    const hoverSlot = hovered < 0 ? null : (order.indexOf(hovered) ?? null);
    if (hoverSlot !== lastHoverSlot) {
      lastHoverSlot = hoverSlot;
      // 我是被抽的人 → 我的手牌 = 对方的取牌扇，走取牌扇那条通道；
      // 否则我的手牌是"行动者的手牌"，走旁观扇那条通道
      const victim = v.pending?.kind === 'stealCard' && v.pending.steal?.from === v.you;
      online.side.broadcast(victim ? SIDE_FAN_HOVER : SIDE_HOVER, { slot: hoverSlot });
    }
  }

  const rebuildSoon = (): void => {
    if (dirty) return;
    dirty = true;
    queueMicrotask(() => {
      dirty = false;
      if (destroyed) return;
      build();
    });
  };

  /** 抽牌人被抽走牌 → 翻一张给他看 */
  function playFlip(uid: number): void {
    const v = view();
    if (v === null) return;
    const pooled = [...v.yourHand, ...v.discardPile].find((card) => card.uid === uid);
    const def = pooled === undefined ? undefined : ctx.registry.get(pooled.title);
    if (def === undefined) return;
    const flip = new CardFlip({ renderer, def, faces: ctx.faces, height: 132 });
    const center = ensureChrome().center();
    flip.view.position.set(center.x - flip.size.width / 2, center.y - flip.size.height / 2);
    effects.addChild(flip.view);
    flips.push(flip);
    flip.flip();
  }

  build();

  return {
    container,
    onEnter() {
      // ★ 一局一份账本。`reset()` 会把"这局抽过几次异常 / 牌堆基线 / isFinal 闩"
      //    全清掉，然后立刻按当前视图重新报一次 —— 重进这一屏（比如从设置里
      //    退出来再进来）不会把上一局的紧张度带过来。
      ctx.energy?.reset();
      const currentView = view();
    reportEnergy(ctx.energy, currentView, ctx.registry);
    if (currentView !== null) syncOtherDying(currentView);
      offView = session.onView(() => rebuildSoon());
      // ★ dev：把这一局挂到“挂号处”（热座那屏也挂了），DEV_TOOLS.game 就能摸到
      if (import.meta.env.DEV) {
        unpublish = publishGame({
          label: 'online',
          view: () => view(),
          // 客户端没有 GameState，算不出合法动作清单；联机的动作由 send() 直接发
          options: () => [],
          // 联机是异步发给房主；这里不 await，回退由 send() 自己处理
          act: (action) => {
            void send(action as GameAction);
            return null;
          },
          buttons: () => chrome?.buttons() ?? null,
          effects: () => ({
            plays: plays.map((reveal) => ({
              phase: reveal.phase,
              phaseMs: Math.round(reveal.phaseMs),
              elapsed: Math.round(reveal.elapsed),
              cancelled: reveal.cancelled,
            })),
            reveals: reveals.map((entry) => ({
              uid: entry.uid,
              handedOff: entry.handedOff,
              dying: entry.dying,
            })),
            arriving: [...arriving],
            inFlight: [...inFlight],
            pending: pendingReveals.length,
            handLive,
          }),
          // ★ 联机只有房主那侧有权威状态；客户端返回 null
          state: () => (session.isHost ? session.debug().state : null),
          table: () => ({
            isHost: session.isHost,
            resolution: view()?.resolution ?? null,
            pending: view()?.pending ?? null,
            turn: view()?.turn ?? null,
            armed: armed ?? null,
            handLive,
            handPlayable: hand.playable,
            handHeld: hand.held,
            handOrder: [...hand.order],
            handDragOut: hand.dragOut().map((def) => def.id),
          }),
          session: () => session.debug(),
        });
      }

      /**
       * ★ **房主问我要手牌顺序。**
       *
       * 两张同名结算时，房主需要知道"第 index 位是哪张牌"——
       * 而那个顺序**只有我知道**（联机时理牌不发数据）。
       * 所以房主主动索要一次，这里把本机的**视觉顺序**原样报回去。
       *
       * ⚠️ 报的是 `hand.order` 映射回 uid，不是 `view.yourHand` 的原始顺序 ——
       * 玩家拖过牌之后，两者不一样。
       */
      offAsk = session.onAsk(STEAL_ORDER_METHOD, z.object({}), () => {
        const v = view();
        if (v === null) return { order: [] };
        const order = hand?.order;
        const uids = v.yourHand.map((card) => card.uid);
        if (order === undefined || order.length !== uids.length) return { order: uids };
        return {
          order: order.map((i) => uids[i]).filter((uid): uid is CardUid => uid !== undefined),
        };
      });

      /**
       * ★ **旁路通道的接收端。**
       *
       * 两条数据都来自**直接广播**（不经过房主），而且都只用来摆画面：
       *
       * | 收到 | 干什么 |
       * | --- | --- |
       * | `SIDE_HOVER`（对面指着第 N 张牌背） | 我这边对应的那张牌抬起来 |
       * | `SIDE_DRAG`（对面把第 N 张拎到了哪儿） | 我这一扇牌背照着挪过去 |
       *
       * ⚠️ 载荷里**没有任何牌的 id** —— 所有人看到的都只是"没有实际数据的
       * 牌背在动"（评审原话）。所以收到的人再看也看不出牌是什么。
       */
      offSideHover = online.side.on(SIDE_HOVER, HoverFrameSchema, (frame, from) => {
        const v = view();
        if (v === null || from === v.you) return;
        // ★ 观众：正在行动的人 hover 了**他自己手里**的第几位 → 旁观扇抬起来
        if (from === v.turn?.player) spectator.setHover(frame.slot);
      });
      /**
       * ★ **取牌扇 ↔ 被抽人的手牌**那条 hover 通道。
       *
       * 和 `SIDE_HOVER`（行动者自己的手牌 → 旁观扇）**分开**，否则两种 hover
       * 会互相触发：抽牌人 hover 取牌扇时，被抽人的旁观扇和手牌会一起抬起来。
       */
      offSideFanHover = online.side.on(SIDE_FAN_HOVER, HoverFrameSchema, (frame, from) => {
        const v = view();
        if (v === null || from === v.you) return;
        // 我是被抽的人 → 抬我对应的那张**手牌**
        const victim = v.pending?.kind === 'stealCard' && v.pending.steal?.from === v.you;
        if (victim) {
          const order = hand?.order ?? [];
          const index = frame.slot === null ? null : (order[frame.slot] ?? null);
          hand?.setRemoteHover(index);
          return;
        }
        // ★ 收回的消息（slot === null）必须无条件清掉手牌上的远端 hover ——
        //   抽牌结束那一刻 pending 已经不在了，走不到上面那一支；不清的话
        //   对方“按住”我某一张留下的高亮会一直挂到下一次抽牌（用户报的）。
        if (frame.slot === null) {
          hand?.setRemoteHover(null);
          chrome?.fan()?.setHover(null);
          return;
        }
        // 我是抽牌的人（或旁观者）→ 抬取牌扇里对应的牌背
        chrome?.fan()?.setHover(frame.slot);
      });

      offSideDrag = online.side.on(SIDE_DRAG, DragFrameSchema, (frame, from) => {
        const v = view();
        if (v === null || from === v.you) return;
        // ★ 观众：顶部那根扇照着重现同一段位移
        if (from === v.turn?.player) spectator.setDrag(frame.slot, frame.x, frame.y, frame.armed, frame.title ?? null);
        chrome?.fan()?.setDrag(frame.slot, frame.x, frame.y);
      });
      offSideReorder = online.side.on(SIDE_REORDER, ReorderFrameSchema, (frame, from) => {
        const v = view();
        if (v === null || from === v.you) return;
        if (from === v.turn?.player) spectator.reorder(frame.from, frame.to);
        chrome?.fan()?.reorder(frame.from, frame.to);
      });
      /**
       * ★ **观众：数量 + “按下露的那张脸”。**
       *
       * ⚠️ 只在 from **就是当前行动者**时应用 —— 旁人也在广播自己的手牌，
       *    但观众那根扇只该镜像"轮到谁"。
       */
      offSideHand = online.side.on(SIDE_HAND, HandCountSchema, (frame, from) => {
        const v = view();
        if (v === null || from === v.you) return;
        if (from === v.turn?.player) spectator.setCount(frame.count);
      });
      offSideReveal = online.side.on(SIDE_REVEAL, RevealFrameSchema, (frame, from) => {
        const v = view();
        if (v === null || from === v.you) return;
        if (from === v.turn?.player) spectator.setReveal(frame.slot, frame.title);
      });
      /**
       * ★ **[断点] 预示：谁在把打断牌往屏幕中心拖。**
       *
       * 每个人都会看到（除了拖的人自己）；入场角由 `InterruptRift` 按同时活跃
       * 的发送者集合均分，所以不会两张叠在一起。`progress: 0` = 松手收回。
       */
      offSideInterrupt = online.side.on(SIDE_INTERRUPT, InterruptFrameSchema, (frame, from) => {
        if (from === view()?.you) return;
        if (frame.progress <= 0) rifts.clear(from);
        else rifts.set(from, frame.progress);
      });
      offEvent = session.onEvent((events) => {
        for (const event of events) {
          if (event.t === 'card/drawn') {
            const def = defOfUid(event.card);
            if (def !== undefined) {
              pendingReveals.push({ uid: event.card, def, hold: false, actor: event.player });
            }
            continue;
          }
          if (event.t === 'card/stolen') playFlip(event.card);
          // ★ 「异常」= 那张会死人的牌（`packs/vanilla` 里的 Exception）。
          //    评分表里「每抽到过一次异常 +0.5」靠这一条累加 —— 它是**事件**，
          //    不是视图能看出来的（牌抽完就被塞回牌堆了，视图里没有痕迹）。
          //    抽到它的那一段动画停在屏幕正中（= 濒死）
          if (event.t === 'lethal/drawn') {
            ctx.energy?.noteExceptionDraw();
            const def = defOfUid(event.card);
            if (def !== undefined) {
              pendingReveals.push({ uid: event.card, def, hold: true, actor: event.player });
            }
          }
        }
        rebuildSoon();
      });
      offStatus = session.onStatus(() => {
        rebuildSoon();
      });
      /**
       * 协议错误**只记日志、不上屏**（评审：玩家看到回退是可接受的）。
       * 挂一个显式订阅是为了让日志里带上下文；不挂的话 Session 也会
       * `console.error`，但那样和"没人管"没有区别。
       */
      offError = session.onError((error) => {
        console.warn(t('game.online.protocolError'), error);
      });
      rebuildSoon();
    },
    onExit() {
      unpublish?.();
      unpublish = null;
      // 离开对局 → 音乐回大厅（`reset()` 会把 `bgmPort` 推回 `PRE_GAME_BGM_PARAMS`）
      ctx.energy?.reset();
      offView?.();
      offEvent?.();
      offStatus?.();
      offAsk?.();
      offError?.();
      offSideHover?.();
      offSideFanHover?.();
      offSideDrag?.();
      offSideReorder?.();
      offSideHand?.();
      offSideReveal?.();
      offSideInterrupt?.();
      offView = offEvent = offStatus = null;
      offAsk = offError = offSideHover = offSideFanHover = offSideDrag = offSideReorder = null;
      offSideHand = offSideReveal = offSideInterrupt = null;
    },
    update() {
      flushHandOrder();
    },
    /** 音乐敲了一下 → 手牌和牌背一起跳一下（和热座那一屏同一件事） */
    onPulse() {
      hand?.pulse();
      // ★ 停在正中那张牌也跟着鼓动（和手牌同一个入口）
      centeredStage.pulse();
      chrome?.fan()?.pulse();
      spectator.pulse();
    },
    updateFrame(ticker: Ticker) {
      hand.update(ticker);
      spectator.update(ticker);
      rifts.update(ticker);
      // ★ 浮在正中那张牌每帧推一次（摆动 / hover / 淡出），再问悬停说明
      // ★ 塞回异常：正中那张牌跟随指针（无按键也处于拖拽态）
      const insertingNow = view()?.pending?.kind === 'insertLethal';
      centeredStage.setFollow(insertingNow);
      centeredStage.setVisible(!insertingNow);
      centeredStage.update(ticker, ctx.energy?.isDying === true);
      // ★ 旁观濒死那张牌：从 0 长到最终大小
      if (otherDyingCard !== null && otherDyingGrow < 1) {
        otherDyingGrow = Math.min(1, otherDyingGrow + ticker.deltaMS / 240);
        otherDyingCard.scale.set(otherDyingGrow);
      }
      particles.update(ticker.deltaMS);
      // ⚠️ 必须在 `hand.update()` **之后**：hover 是那一帧算出来的
      syncTooltip(ticker.deltaMS);
      chrome?.updateFrame(ticker.deltaMS);
      // ★ 记牌器是模态窗口：开着时手牌不吃指针
      hand?.setInteractive(!(chrome?.trackerOpen() ?? false));
      hand?.setPlayable(handLive);
      for (let i = flips.length - 1; i >= 0; i--) {
        const flip = flips[i];
        if (flip === undefined || !flip.finished) continue;
        flip.destroy();
        flips.splice(i, 1);
      }
      updateReveals(ticker);
      // 出牌动画：每帧按最新视图喂"取消与否"，走完就销毁
      updatePlays(ticker);
    },
    onKey(event) {
      if (event.key !== 'Escape') return false;
      if (chrome?.chooserOpen() === true) {
        chrome.closeChooser();
        return true;
      }
      // 记牌器也盖着整屏，先关它
      if (chrome?.trackerOpen() === true) {
        chrome.closeTracker();
        return true;
      }
      // 先作废"选了一半的那一手"（牌回手上），再开设置 ——
      // 不然 Esc 会同时干两件事
      if (selection !== null) {
        cancelSelection();
        refresh();
        return true;
      }
      // 联机时不"退出对局"—— 只是把设置盖上来（对局还在跑）
      ctx.stack.openOverlay(OVERLAY.settings);
      return true;
    },
    onGamepad(intent) {
      switch (intent) {
        case 'menu':
          ctx.stack.openOverlay(OVERLAY.settings);
          return true;
        case 'endTurn':
          chrome?.endTurn();
          return true;
        case 'tracker':
          chrome?.toggleTracker();
          return true;
        default:
          return false;
      }
    },
    rebuild() {
      build();
    },
    destroy() {
      destroyed = true;
      unpublish?.();
      unpublish = null;
      ctx.energy?.reset();
      offView?.();
      offEvent?.();
      offStatus?.();
      offAsk?.();
      offError?.();
      offSideHover?.();
      offSideFanHover?.();
      offSideDrag?.();
      offSideReorder?.();
      offSideHand?.();
      offSideReveal?.();
      offSideInterrupt?.();
      offView = offEvent = offStatus = null;
      offAsk = offError = offSideHover = offSideFanHover = offSideDrag = offSideReorder = null;
      offSideHand = offSideReveal = offSideInterrupt = null;
      spectator.destroy();
      rifts.destroy();
      chrome?.destroy();
      chrome = null;
      for (const flip of flips) flip.destroy();
      flips.length = 0;
      for (const entry of reveals) entry.view.destroy();
      reveals.length = 0;
      for (const reveal of plays) reveal.destroy();
      plays.length = 0;
      lastPlaySignature = null;
      otherDying.destroy({ children: true });
      centeredStage.destroy();
      inFlight = [];
      arriving = [];
      pendingReveals = [];
      hand.cancelArrival();
      // ⚠️ 压住的濒死要放掉：留着的话下一次抽到致命牌就没有任何反应了
      ctx.energy?.holdDying(false);
      tooltip.destroy();
      hand.destroy();
      container.destroy({ children: true });
    },
    sceneCards() {
      return hand.cards;
    },
  };
}

